import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, FileText, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../data/constants";
import { formatPostDate } from "../blog/blogHelpers";

const AdminBlogPage = () => {
  const { authedAxios } = useAuth();
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    authedAxios({ url: `${API}/admin/blog/posts` })
      .then(({ data }) => setPosts(data))
      .catch(() => setError("Could not load blog posts."));
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = filter === "all" ? posts : posts.filter((p) => p.status === filter);

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-dark">Blog Posts</h1>
          <p className="text-gray-medium">Write, publish, and manage blog content.</p>
        </div>
        <Link to="/admin/blog/new" className="btn-primary inline-flex items-center gap-2" data-testid="blog-new-btn">
          <Plus size={16} /> New post
        </Link>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="flex gap-2 mb-4">
        {[
          { key: "all", label: `All (${posts.length})` },
          { key: "published", label: `Published (${posts.filter((p) => p.status === "published").length})` },
          { key: "draft", label: `Drafts (${posts.filter((p) => p.status === "draft").length})` }
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              filter === f.key ? "bg-orange-primary text-white" : "bg-white text-gray-dark hover:bg-gray-light"
            }`}
            data-testid={`blog-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-gray-medium">
            <FileText className="mx-auto mb-3 text-orange-primary/50" size={36} />
            No posts yet. Click <strong>New post</strong> to write your first one.
          </div>
        )}
        {filtered.map((p) => (
          <Link
            key={p.id}
            to={`/admin/blog/${p.id}/edit`}
            className="bg-white rounded-2xl shadow-card p-5 flex items-start gap-4 hover:shadow-card-hover transition-shadow block"
            data-testid={`blog-row-${p.id}`}
          >
            {p.cover_image_url ? (
              <img src={p.cover_image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-orange-light flex items-center justify-center flex-shrink-0">
                <FileText className="text-orange-primary" size={24} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display font-bold text-gray-dark">{p.title}</p>
                {p.status === "published" ? (
                  <span className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Eye size={11} /> Published</span>
                ) : (
                  <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><EyeOff size={11} /> Draft</span>
                )}
                {p.category && <span className="text-xs bg-orange-primary/10 text-orange-primary rounded-full px-2 py-0.5">{p.category}</span>}
              </div>
              {p.excerpt && <p className="text-sm text-gray-medium mt-1 line-clamp-2">{p.excerpt}</p>}
              <p className="text-xs text-gray-medium/70 mt-2">
                /blog/{p.slug} · {p.status === "published" ? `Published ${formatPostDate(p.published_at)}` : `Updated ${formatPostDate(p.updated_at)}`}
                {typeof p.view_count === "number" && p.status === "published" ? ` · ${p.view_count} views` : ""}
              </p>
            </div>
            <Pencil size={16} className="text-gray-medium flex-shrink-0 mt-2" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminBlogPage;
